<template>
  <div class="rd-screen">
    <report-tools :copy="sections.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state v-else-if="!sections.length" icon="mdi-store-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <template v-else>
      <section v-for="section in sections" :key="section.key" class="mr-company">
        <div class="rd-section mr-company__head">
          <div class="mr-company__title">
            <h2>{{ section.name }}</h2>
          </div>
          <span class="rd-num">{{ section.summary }}</span>
        </div>

        <ui-table
          variant="report"
          :columns="columns"
          :rows="section.rows"
          :row-key="(row) => row.key"
          :totals="sectionTotals(section)"
          :total-label="$t('total')"
        >
          <template #head-shift="{ column }">
            <report-shift-tag :shift="column.shift.id"></report-shift-tag>
          </template>

          <template #cell-total="{ row }"><b>{{ row.total }}</b></template>
        </ui-table>
      </section>
    </template>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabRestaurantMeals from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabRestaurantMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import hasPlural from "@/components/report/shared/hasPlural";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Restorani obroci firme "više restorana" na računaru: po restoranu obroci × smena sa zbirom. Restorani, smene,
 * period i pretragu (naziv ili tip obroka) čita iz zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz
 * starog taba.
 */
export default {
  name: "CompanyDesktopMultiRestaurantMeals",
  extends: CompanyMultiRestaurantTabRestaurantMeals,
  mixins: [hasMultiFeed, hasPlural],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Kolone tabele (iste za svaki restoran): bez sortiranja. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), name: true },
        { key: "type", label: this.$t("type"), muted: true, value: (row) => row.type || "–" },
        ...this.visibleShifts.map((shift) => ({
          key: shift.key,
          slot: "shift",
          shift,
          align: "end",
          num: true,
          value: (row) => row.text[shift.key],
          zero: (row) => !row[shift.key],
        })),
        { key: "total", label: this.$t("total"), align: "end", num: true },
      ];
    },
    busy() {
      return this.isLoading;
    },
    hasRows() {
      return this.sections.length > 0;
    },
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.shiftsWithData.includes(shift.id) &&
          (shift.id < 4 ||
            this.restaurantCards.some((card) =>
              card.meals.some((meal) => (meal[shift.key] && meal[shift.key].totalQuantity) > 0)
            ))
      );
    },
    /** Restorani sa obrocima posle pretrage (naziv ili tip obroka). */
    sections() {
      const search = this.$store.state.report.filters.search;
      return this.restaurantCards
        .map((card) => {
          const found = search ? this.customSearch(card.meals, [{ value: "name" }, { value: "type" }], search) : card.meals;
          const rows = found.map((meal, index) => {
            const row = {
              key: `${meal.id || meal.name}-${index}`,
              name: this.toLatin(meal.name || ""),
              type: this.typeText(meal.type),
              total: meal.total || 0,
              text: {},
            };
            SHIFTS.forEach((shift) => {
              row[shift.key] = (meal[shift.key] && meal[shift.key].totalQuantity) || 0;
              row.text[shift.key] = this.formatShiftQuantity(meal[shift.key]);
            });
            return row;
          });
          const sums = {};
          SHIFTS.forEach((shift) => {
            const sum = { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
            found.forEach((meal) => {
              const data = meal[shift.key] || {};
              sum.customerQuantity += data.customerQuantity || 0;
              sum.restaurantQuantity += data.restaurantQuantity || 0;
              sum.totalQuantity += data.totalQuantity || 0;
            });
            sums[shift.key] = this.formatShiftQuantity(sum);
          });
          const total = rows.reduce((sum, row) => sum + row.total, 0);
          return {
            key: String(card.id),
            name: card.name,
            rows,
            sums,
            total,
            summary: this.pluralT("report_mobile.meals_n", total),
          };
        })
        .filter((section) => section.rows.length);
    },
    total() {
      return this.sections.reduce((sum, section) => sum + section.total, 0);
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.total, tone: "inverse" },
        { key: "restaurants", label: this.$t("restaurants"), value: this.sections.length },
      ];
    },
  },
  methods: {
    /** Zbirni red restorana: smene i ukupno. */
    sectionTotals(section) {
      const totals = { total: section.total };
      this.visibleShifts.forEach((shift) => {
        totals[shift.key] = section.sums[shift.key];
      });
      return totals;
    },
    async copyTable() {
      const headers = [
        this.$t("restaurant"),
        this.$t("meal"),
        this.$t("type"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.sections.flatMap((section) =>
        section.rows.map((row) => [section.name, row.name, row.type, ...this.visibleShifts.map((shift) => row.text[shift.key]), row.total])
      );
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
